import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { api } from '../apiClient';
import { X, Check } from 'lucide-react';

// Use your public key here. In production use env var.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
// TODO: Replace with actual key or ensure it's in env var and passed correctly.
// For now I'll use a placeholder and assume the user has it in env.

const CheckoutForm = ({ selectedPackage, onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setLoading(true);

        try {
            // 1. Create Payment Intent
            const { data } = await api.post('/create-credit-payment-intent', {
                credits_amount: selectedPackage.credits,
                price_amount: selectedPackage.price,
                currency: "usd"
            });

            const result = await stripe.confirmCardPayment(data.clientSecret, {
                payment_method: {
                    card: elements.getElement(CardElement),
                    billing_details: {
                        name: 'T-Shirt App User', //Ideally user's name
                    },
                },
            });

            if (result.error) {
                onError(result.error.message);
            } else {
                if (result.paymentIntent.status === 'succeeded') {
                    // Manually confirm to ensure immediate credit update
                    try {
                        await api.post('/confirm-credit-purchase', {
                            payment_intent_id: result.paymentIntent.id
                        });
                    } catch (confirmError) {
                        console.error("Manual confirmation failed, relying on webhook:", confirmError);
                    }
                    onSuccess();
                }
            }
        } catch (error) {
            console.error("Payment failed:", error);
            onError(error.response?.data?.detail || "Payment failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="mt-4">
            <div className="p-3 border rounded-md mb-4 bg-white">
                <CardElement options={{
                    hidePostalCode: true,
                    style: {
                        base: {
                            fontSize: '16px',
                            color: '#424770',
                            '::placeholder': {
                                color: '#aab7c4',
                            },
                        },
                        invalid: {
                            color: '#9e2146',
                        },
                    },
                }} />
            </div>
            <button
                type="submit"
                disabled={!stripe || loading}
                className={`w-full py-3 px-4 rounded-md font-bold text-white transition ${loading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                    }`}
            >
                {loading ? 'Processing...' : `Pay $${selectedPackage.price}`}
            </button>
        </form>
    );
};

const CreditPurchaseModal = ({ isOpen, onClose, onSuccess }) => {
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const packages = [
        { id: 1, credits: 50, price: 5.00, label: "Starter" },
        { id: 2, credits: 110, price: 10.00, label: "Popular" }, // 10% bonus
        { id: 3, credits: 250, price: 20.00, label: "Pro" },     // 25% bonus
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition"
                >
                    <X size={24} />
                </button>

                <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Buy Credits</h2>
                    <p className="text-gray-600 mb-6">Generate more awesome designs!</p>

                    {successMsg ? (
                        <div className="text-center py-8">
                            <div className="mx-auto flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                                <Check size={32} className="text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Purchase Successful!</h3>
                            <p className="text-gray-600 mb-6">Your credits have been added.</p>
                            <button
                                onClick={onSuccess}
                                className="w-full py-3 px-4 bg-primary text-white rounded-md font-bold"
                            >
                                Continue
                            </button>
                        </div>
                    ) : (
                        <>
                            {!selectedPackage ? (
                                <div className="space-y-3">
                                    {packages.map((pkg) => (
                                        <div
                                            key={pkg.id}
                                            onClick={() => setSelectedPackage(pkg)}
                                            className="border-2 border-gray-100 rounded-lg p-4 cursor-pointer hover:border-primary hover:bg-blue-50 transition flex justify-between items-center group"
                                        >
                                            <div>
                                                <div className="font-bold text-gray-800 text-lg">{pkg.credits} Credits</div>
                                                <div className="text-sm text-gray-500">{pkg.label}</div>
                                            </div>
                                            <div className="font-bold text-primary group-hover:scale-105 transition">
                                                ${pkg.price.toFixed(2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div>
                                    <div className="flex justify-between items-center mb-6 pb-4 border-b">
                                        <div>
                                            <div className="font-bold text-gray-800">{selectedPackage.credits} Credits</div>
                                            <button
                                                onClick={() => setSelectedPackage(null)}
                                                className="text-sm text-primary hover:underline"
                                            >
                                                Change Package
                                            </button>
                                        </div>
                                        <div className="font-bold text-xl">${selectedPackage.price.toFixed(2)}</div>
                                    </div>

                                    {error && (
                                        <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm mb-4">
                                            {error}
                                        </div>
                                    )}

                                    {/* Make sure to retrieve key from env in a real app */}
                                    <Elements stripe={stripePromise}>
                                        <CheckoutForm
                                            selectedPackage={selectedPackage}
                                            onSuccess={() => {
                                                setSuccessMsg(true);
                                                window.dispatchEvent(new Event('credits-updated'));
                                            }}
                                            onError={setError}
                                        />
                                    </Elements>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreditPurchaseModal;
